import { RightOutlined } from "@ant-design/icons";
import type { CardProps } from "antd";
import { Button, Card, Flex, Image, Typography } from "antd";
import type { FC } from "react";
import CountUp from "react-countup";
import { useNavigate } from "react-router";

import GetStartedImage from "../assets/get-started.png";
import type { ApplicationInfoDto, CampaignMetricsDto, OrgUnitMetricsDto, TerminalMetricsDto } from "../types/Dashboard";

/**
 * WelcomeCard - Clean and simple welcome section with background illustration
 */
export const WelcomeCard: FC<WelcomeCardProps> = ({ applicationInfo, campaignMetrics, orgUnitMetrics, terminalMetrics, ...cardProps }) => {
    const navigate = useNavigate();

    const handleGetStarted = () => {
        navigate("/campaigns");
    };

    const running = campaignMetrics.runningCampaignsCount;
    const sites = orgUnitMetrics.totalSitesCount;
    const affected = terminalMetrics.affectedTerminalsCount;

    // Build welcome message based on running campaigns and affected terminals
    const renderMessage = () => {
        if (running === 0 && affected === 0) {
            return (
                <Typography.Text>
                    Your payment infrastructure is running smoothly across <CountUp end={sites} /> site{sites !== 1 ? "s" : ""}. All
                    terminals are up to date with no pending deployments.
                </Typography.Text>
            );
        }

        if (running === 0 && affected > 0) {
            return (
                <Typography.Text>
                    You have <CountUp end={affected} /> terminal{affected !== 1 ? "s" : ""} awaiting updates across <CountUp end={sites} />{" "}
                    site{sites !== 1 ? "s" : ""}. Consider creating a campaign to deploy the latest configurations.
                </Typography.Text>
            );
        }

        if (running > 0 && affected === 0) {
            return (
                <Typography.Text>
                    You have <CountUp end={running} /> running campaign{running !== 1 ? "s" : ""} across <CountUp end={sites} /> site
                    {sites !== 1 ? "s" : ""}. All terminals are up to date. Great work managing your deployments!
                </Typography.Text>
            );
        }

        // running > 0 && affected > 0
        return (
            <Typography.Text>
                You have <CountUp end={running} /> running campaign{running !== 1 ? "s" : ""} across <CountUp end={sites} /> site
                {sites !== 1 ? "s" : ""}, with <CountUp end={affected} /> terminal{affected !== 1 ? "s" : ""} still pending updates. Keep
                monitoring your deployments to ensure all terminals are updated.
            </Typography.Text>
        );
    };

    return (
        <Card {...cardProps} style={{ backgroundColor: "#FFFFFF", height: "100%", ...cardProps.style }}>
            <Flex justify="space-between" align="center" gap="middle" style={{ height: "100%" }}>
                <Flex vertical gap="large" align="flex-start">
                    <Typography.Title level={4} style={{ margin: 0 }}>
                        Welcome back, {applicationInfo.userName.split("@")[0] || applicationInfo.userName}!
                    </Typography.Title>
                    {renderMessage()}
                    <Button type="primary" size="middle" onClick={handleGetStarted}>
                        Get started <RightOutlined />
                    </Button>
                </Flex>
                <Image src={GetStartedImage} height={180} preview={false} style={{ objectFit: "cover" }} />
            </Flex>
        </Card>
    );
};

interface WelcomeCardProps extends CardProps {
    applicationInfo: ApplicationInfoDto;
    campaignMetrics: CampaignMetricsDto;
    orgUnitMetrics: OrgUnitMetricsDto;
    terminalMetrics: TerminalMetricsDto;
}
